import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Loader2, Plus, Edit2, Trash2, Download, MapPin, Phone, Briefcase } from "lucide-react";
import { Modal, In } from "@/pages/Workers";

export default function FieldStaff() {
  const [staff, setStaff] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("master");
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [payrollEdit, setPayrollEdit] = useState(null);

  const load = async () => {
    setLoading(true);
    const [s, e] = await Promise.all([
      api.get("/field-staff"),
      api.get("/employees"),
    ]);
    setStaff(s.data); setEmployees(e.data);
    if (tab === "payroll") {
      const p = await api.get("/fa-payroll", { params: { month } });
      setPayroll(p.data);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [tab, month]);

  const remove = async (id) => {
    if (!window.confirm("Delete this field staff?")) return;
    await api.delete(`/field-staff/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="field-staff-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Field Associates</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Field Staff (FA)</h1>
          <p className="text-stone-600 mt-1 text-sm">Dealer-assigned associates with their own payroll cycle.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-fa-button"
                className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
          <Plus className="w-4 h-4" /> Add Field Staff
        </button>
      </header>

      <div className="flex gap-2 border-b border-stone-200">
        {["master", "payroll"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`fa-tab-${t}`}
                  className={`px-5 py-2 text-sm font-medium border-b-2 ${tab === t ? "border-primary text-primary" : "border-transparent text-stone-500"}`}>
            {t === "master" ? "Master" : "Monthly Salary"}
          </button>
        ))}
      </div>

      {tab === "payroll" && (
        <div className="flex items-center gap-3">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                 className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white" data-testid="fa-month" />
          <div className="ml-auto text-sm">
            <span className="text-stone-500">Total Payable: </span>
            <span className="font-heading text-lg font-bold" style={{ color: "var(--color-primary)" }}>
              {formatINR(payroll.reduce((s, r) => s + (r.total_payable || 0), 0))}
            </span>
          </div>
        </div>
      )}

      {loading ? <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        tab === "master" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="fa-grid">
            {staff.length === 0 && <div className="text-sm text-stone-500">No field staff yet.</div>}
            {staff.map((s) => (
              <div key={s.id} className="card-flat" data-testid={`fa-${s.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>{s.fa_code}</div>
                    <div className="font-heading text-lg font-bold mt-1">{s.name}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{s.dealer_name || "—"}</div>
                  </div>
                  <span className="status-pill capitalize" style={{ background: s.status === "active" ? "var(--color-primary-light)" : "#F4F1DE", color: s.status === "active" ? "var(--color-primary)" : "#57534E" }}>
                    {s.status}
                  </span>
                </div>
                <div className="mt-3 space-y-1.5 text-xs">
                  {s.area && <div className="flex items-center gap-2 text-stone-600"><MapPin className="w-3 h-3" /> {s.area}</div>}
                  {s.mobile && <div className="flex items-center gap-2 text-stone-600"><Phone className="w-3 h-3" /> {s.mobile}</div>}
                  {s.reporting_to_name && <div className="flex items-center gap-2 text-stone-600"><Briefcase className="w-3 h-3" /> Reports to {s.reporting_to_name}</div>}
                </div>
                <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between">
                  <div>
                    <div className="kpi-label">CTC</div>
                    <div className="font-semibold mt-0.5">{formatINR(s.ctc_annual)}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setEditing(s)} className="p-1.5 rounded hover:bg-stone-100"><Edit2 className="w-4 h-4 text-stone-500" /></button>
                    <button onClick={() => remove(s.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card-flat p-0 overflow-x-auto">
            <table className="w-full text-xs border-collapse" data-testid="fa-payroll-table">
              <thead>
                <tr><Th>Code</Th><Th>Name</Th><Th>Dealer</Th><Th>Area</Th>
                  <Th>CTC</Th><Th>Monthly</Th><Th bg="#FEFAE0">Days</Th>
                  <Th bg="#FCEFE9">Earned</Th><Th>DA</Th><Th>Incentive</Th>
                  <Th>Deductions</Th><Th bg="#D8F3DC">Total Payable</Th><Th>Remarks</Th><Th></Th></tr>
              </thead>
              <tbody>
                {payroll.length === 0 && <tr><td colSpan="14" className="py-10 text-center text-stone-500">No records for {month}.</td></tr>}
                {payroll.map((r) => (
                  <tr key={r.id} className="border-t border-stone-100">
                    <Td mono>{r.fa_code}</Td><Td strong>{r.name}</Td>
                    <Td>{r.dealer_name || "—"}</Td><Td>{r.area || "—"}</Td>
                    <Td right>{r.ctc_annual?.toLocaleString("en-IN")}</Td>
                    <Td right>{r.monthly?.toLocaleString("en-IN")}</Td>
                    <Td center bg="#FEFAE0">{r.days_present}</Td>
                    <Td right bg="#FCEFE9" strong>{r.earned_salary?.toLocaleString("en-IN")}</Td>
                    <Td right>{r.da_amount?.toLocaleString("en-IN") || 0}</Td>
                    <Td right>{r.incentive?.toLocaleString("en-IN") || 0}</Td>
                    <Td right className="text-orange-700">-{r.deductions?.toLocaleString("en-IN") || 0}</Td>
                    <Td right bg="#D8F3DC" strong style={{ color: "var(--color-primary)" }}>{r.total_payable?.toLocaleString("en-IN")}</Td>
                    <Td className="max-w-[10rem] truncate">{r.remarks || "—"}</Td>
                    <Td><button onClick={() => setPayrollEdit(r)} className="p-1 rounded hover:bg-stone-100"><Edit2 className="w-3.5 h-3.5" /></button></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {(showAdd || editing) && (
        <FaForm employees={employees} initial={editing}
                onClose={() => { setShowAdd(false); setEditing(null); }}
                onSaved={() => { setShowAdd(false); setEditing(null); load(); }} />
      )}
      {payrollEdit && (
        <FaPayrollEdit row={payrollEdit} onClose={() => setPayrollEdit(null)}
                        onSaved={() => { setPayrollEdit(null); load(); }} />
      )}
    </div>
  );
}

function Th({ children, bg }) {
  return <th className="px-2 py-2 border border-stone-200 whitespace-nowrap"
             style={{ background: bg || "#F4F1DE", fontSize: 10 }}>{children}</th>;
}
function Td({ children, center, right, mono, strong, bg, className = "", style }) {
  return <td className={`px-2 py-1.5 border border-stone-100 ${center ? "text-center" : right ? "text-right" : ""} ${mono ? "font-mono" : ""} ${strong ? "font-semibold" : ""} ${className}`}
             style={{ background: bg, fontSize: 11, ...style }}>{children}</td>;
}

function FaForm({ employees, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    name: "", dealer_name: "", area: "", reporting_to_id: "",
    mobile: "", email: "", ctc_annual: 240000, fixed_da: 0, incentive_target: 0,
    aadhaar: "", bank_name: "", account_number: "", ifsc: "",
    joining_date: new Date().toISOString().slice(0, 10), status: "active", remarks: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const body = {
      ...form,
      ctc_annual: Number(form.ctc_annual),
      fixed_da: Number(form.fixed_da),
      incentive_target: Number(form.incentive_target),
      reporting_to_id: form.reporting_to_id || null,
    };
    if (initial) await api.put(`/field-staff/${initial.id}`, body);
    else await api.post("/field-staff", body);
    setBusy(false); onSaved();
  };
  return (
    <Modal title={initial ? "Edit Field Staff" : "Add Field Staff"} onClose={onClose} testid="fa-modal">
      <form onSubmit={submit} className="grid grid-cols-2 gap-3">
        <In label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} required />
        <In label="Dealer Name" v={form.dealer_name} on={(v) => setForm({ ...form, dealer_name: v })} />
        <In label="Area" v={form.area} on={(v) => setForm({ ...form, area: v })} />
        <label className="block"><div className="kpi-label mb-1.5">Reporting To</div>
          <select value={form.reporting_to_id || ""} onChange={(e) => setForm({ ...form, reporting_to_id: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
            <option value="">— None —</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} ({e.designation})</option>)}
          </select>
        </label>
        <In label="Mobile" v={form.mobile} on={(v) => setForm({ ...form, mobile: v })} />
        <In label="Email" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} />
        <In label="CTC Annual ₹" type="number" v={form.ctc_annual} on={(v) => setForm({ ...form, ctc_annual: v })} required />
        <In label="Fixed DA / month ₹" type="number" v={form.fixed_da} on={(v) => setForm({ ...form, fixed_da: v })} />
        <In label="Incentive Target ₹" type="number" v={form.incentive_target} on={(v) => setForm({ ...form, incentive_target: v })} />
        <In label="Aadhaar" v={form.aadhaar} on={(v) => setForm({ ...form, aadhaar: v })} />
        <In label="Bank Name" v={form.bank_name} on={(v) => setForm({ ...form, bank_name: v })} />
        <In label="Account No." v={form.account_number} on={(v) => setForm({ ...form, account_number: v })} />
        <In label="IFSC" v={form.ifsc} on={(v) => setForm({ ...form, ifsc: v })} />
        <In label="Joining Date" type="date" v={form.joining_date} on={(v) => setForm({ ...form, joining_date: v })} />
        <label className="block"><div className="kpi-label mb-1.5">Status</div>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
            <option value="active">Active</option><option value="inactive">Inactive</option>
          </select>
        </label>
        <button type="submit" disabled={busy} data-testid="save-fa-button"
                className="col-span-2 py-2 mt-2 rounded-md text-white font-semibold" style={{ background: "var(--color-primary)" }}>
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
    </Modal>
  );
}

function FaPayrollEdit({ row, onClose, onSaved }) {
  const [form, setForm] = useState({
    days_present: row.days_present || 30,
    da_amount: row.da_amount || 0,
    incentive: row.incentive || 0,
    deductions: row.deductions || 0,
    remarks: row.remarks || "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    await api.put(`/fa-payroll/${row.id}`, {
      fa_id: row.fa_id, month: row.month,
      days_present: Number(form.days_present),
      da_amount: Number(form.da_amount),
      incentive: Number(form.incentive),
      deductions: Number(form.deductions),
      remarks: form.remarks,
    });
    setBusy(false); onSaved();
  };
  return (
    <Modal title={`Edit · ${row.name} · ${row.month}`} onClose={onClose} testid="fa-payroll-modal">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <In label="Days Present (of 30)" type="number" v={form.days_present} on={(v) => setForm({ ...form, days_present: v })} />
          <In label="DA Amount ₹" type="number" v={form.da_amount} on={(v) => setForm({ ...form, da_amount: v })} />
          <In label="Incentive ₹" type="number" v={form.incentive} on={(v) => setForm({ ...form, incentive: v })} />
          <In label="Deductions ₹" type="number" v={form.deductions} on={(v) => setForm({ ...form, deductions: v })} />
        </div>
        <label><div className="kpi-label mb-1.5">Remarks</div>
          <textarea rows="2" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
        </label>
        <button type="submit" disabled={busy} data-testid="save-fa-payroll-button"
                className="w-full py-2 rounded-md text-white font-semibold" style={{ background: "var(--color-primary)" }}>
          {busy ? "Saving…" : "Recalculate & Save"}
        </button>
      </form>
    </Modal>
  );
}
